// ---------- le mot du créateur ----------
// La carte en tête de Réglages (index.html, .maker) : qui fait le jeu, et deux gestes pour
// l'aider — le partager, le noter. Ce module peint ses deux dessins et câble ses deux boutons ;
// le texte vient des dictionnaires (`maker.*`).
//
// POURQUOI ICI. Un jeu fait par une personne et non par un studio, c'est un argument, et c'est
// aussi la raison de la demande : la pub reste discrète par choix, donc ce qui fait vivre le jeu
// est le bouche-à-oreille. La carte s'adresse à TOUS les joueurs, avec le même bouton, et ne
// réclame ni étoiles ni ne promet rien en échange d'une note : deux règles de Google Play.

import { t } from './i18n';
import { buzz, isNative, shareSheet } from './platform';
import { paintAvatar } from './profile';
import { openStoreListing } from './review';
import { PIGMENTS } from '../shared/alphabets/pointille';

const $ = <T extends HTMLElement = HTMLElement>(id: string): T => document.getElementById(id) as T;

/** Le nom que l'auteur a choisi de montrer — le même dans toutes les langues, et jamais son
 *  prénom. L'avatar en est tiré, comme celui de n'importe quel joueur. */
const MAKER = 'tidou44';

/** La boîte du jeu, pour « M'écrire ». Ce n'est pas un secret — elle part dans chaque APK — mais
 *  elle vit ici en une seule occurrence, comme les URLs de production. */
const SUPPORT_EMAIL = 'tidou44@gmail.com';

/**
 * Le lien partagé : TOUJOURS la fiche Play Store, choix de l'auteur. Ce bouton recommande
 * l'application, et c'est l'installation qui fait vivre le jeu ; le lien du jeu web, partagé
 * avant, menait un ami Android sur la version navigateur, sans jamais lui montrer le Store. Le
 * texte qui l'accompagne (`maker.shareText`) dit que le jeu n'existe que sur Android pour le
 * moment : qui reçoit le lien sur un iPhone sait à quoi s'attendre avant de toucher.
 *
 * `utm_source=reglages` est propre à CE bouton (la page de partage d'une feuille porte
 * `partage`, cf. src/share.ts) : la Console Play distingue ainsi les installations qu'il amène.
 * Le referrer est doublement encodé, et il FAUT qu'il le reste — Play le redécode lui-même.
 */
const PLAY_URL =
  'https://play.google.com/store/apps/details?id=fr.tidou44.codigrid' +
  '&referrer=utm_source%3Dreglages%26utm_medium%3Dapp';

/** Les points du coin de la carte : les neuf pigments, qui s'estompent vers le texte. Le même
 *  motif que la carte d'identité de l'écran « Moi », mais en couleurs — ici il n'y a pas de fond
 *  d'accent plein pour porter des points blancs. */
function drawDots(c: HTMLCanvasElement): void {
  const x = c.getContext('2d');
  if (!x) return;
  let k = 0;
  for (let r = 0; r < 8; r++) {
    for (let q = 0; q < 10; q++) {
      const rad = 7 * (1 - Math.hypot(q - 10, r) / 10);
      if (rad < 0.8) continue;
      x.fillStyle = PIGMENTS[(k++ * 5) % PIGMENTS.length].css;
      x.beginPath();
      x.arc(q * 24 + 12, r * 24 + 12, rad, 0, Math.PI * 2);
      x.fill();
    }
  }
}

async function share(): Promise<void> {
  buzz('light');
  const r = await shareSheet({
    title: t('brand.name'),
    text: t('maker.shareText'),
    url: PLAY_URL,
  });
  // Même retour que le partage d'une feuille (ui.ts) : le sélecteur du système est sa propre
  // confirmation, il ne reste à annoncer que le lien parti au presse-papiers sans rien montrer.
  if (r !== 'copié') return;
  const label = $('makerShareLabel');
  label.textContent = t('game.linkCopied');
  window.setTimeout(() => (label.textContent = t('maker.share')), 2000);
}

/** Un mailto et rien d'autre : pas de formulaire à héberger, et le joueur écrit depuis SA boîte —
 *  il recevra la réponse là où il l'attend. Le sujet porte la version : c'est l'information qu'on
 *  ne pense jamais à demander et qui manque à chaque rapport de bug. */
function contact(): void {
  buzz('light');
  const subject = encodeURIComponent(`Codigrid ${__VERSION__}`);
  const url = `mailto:${SUPPORT_EMAIL}?subject=${subject}`;
  // `_system` sort de la WebView pour laisser Android choisir l'application de courrier (même
  // raison que openStoreListing). Sur le web en revanche, window.open traiterait « _system »
  // comme un simple nom de fenêtre : un onglet blanc orphelin resterait derrière le client de
  // courrier. La navigation directe laisse le navigateur passer la main sans rien ouvrir.
  if (isNative()) window.open(url, '_system');
  else location.href = url;
}

export function wireMaker(): void {
  paintAvatar($<HTMLCanvasElement>('makerAvatar'), MAKER);
  drawDots($<HTMLCanvasElement>('makerDots'));
  // Rien à noter depuis le web : la fiche du Store parle d'une application qu'un joueur de
  // navigateur n'a pas installée.
  $('makerRate').hidden = !isNative();
  // Et le texte suit : sur le web, il ne demande pas de noter ce qu'on ne peut pas noter.
  if (!isNative()) $('makerAsk').textContent = t('maker.askWeb');
  $('makerRate').onclick = () => {
    buzz('light');
    openStoreListing();
  };
  $('makerShare').onclick = () => void share();
  $('makerContact').onclick = () => contact();
}
